import { EMPRESA } from "../lib/config";
import { EstadoBadge } from "./EstadoBadge";
import { Package, Truck, MapPin, Phone, CreditCard, User, Building2 } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const fullName = (p) => `${p?.nombre || ""} ${p?.apellidos || ""}`.trim() || "—";

const Party = ({ title, icon: Icon, accent, p, addressLabel }) => (
  <div className="rounded-xl border border-slate-200 overflow-hidden">
    <div className={`px-4 py-2.5 text-white font-bold text-sm flex items-center gap-2 ${accent}`}>
      <Icon className="h-4 w-4" /> {title}
    </div>
    <div className="p-4 space-y-3 bg-slate-50">
      <Row icon={User} label="Nombre" value={fullName(p)} bold />
      <div className="grid grid-cols-2 gap-3">
        <Row icon={CreditCard} label="DNI" value={p?.dni || "—"} />
        <Row icon={Phone} label="Móvil" value={p?.movil || "—"} />
      </div>
      <Row icon={MapPin} label={addressLabel} value={p?.direccion || "—"} />
    </div>
  </div>
);

const Row = ({ icon: Icon, label, value, bold }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`text-slate-900 break-words ${bold ? "font-bold" : "font-medium"}`}>{value}</p>
    </div>
  </div>
);

export const AlbaranDocument = ({ albaran, testid = "albaran-document" }) => {
  const a = albaran;
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 md:px-8 py-6 bg-slate-900 text-white flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight">{EMPRESA.nombre}</h1>
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-slate-300 mt-1">
            Albarán de recogida y entrega
          </p>
          <p className="text-[11px] text-slate-400 mt-1">
            {EMPRESA.persona} · DNI/NIF: {EMPRESA.dni} · {EMPRESA.direccion}
          </p>
        </div>
        <div className="bg-blue-600 rounded-lg px-4 py-3 text-right">
          <p className="text-[10px] uppercase tracking-wide text-blue-200">Albarán Nº</p>
          <p className="font-mono font-bold text-lg" data-testid="doc-numero">{a.numero || "BORRADOR"}</p>
          <p className="text-[11px] text-blue-100 mt-0.5">{fmtDate(a.createdAt)}</p>
        </div>
      </div>

      <div className="px-6 md:px-8 py-3 border-b border-slate-200 flex items-center gap-2">
        <span className="text-xs font-semibold uppercase text-slate-400">Estado:</span>
        <EstadoBadge estado={a.estado} />
      </div>

      {/* Remitente / Destinatario */}
      <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Party title="Recogida · Remitente" icon={Package} accent="bg-blue-600" p={a.remitente} addressLabel="Dirección de recogida" />
        <Party title="Entrega · Destinatario" icon={Truck} accent="bg-violet-600" p={a.destinatario} addressLabel="Dirección de entrega" />
      </div>

      {/* Emisora */}
      <div className="px-6 md:px-8 pb-2">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-start gap-2.5">
          <Building2 className="h-4 w-4 text-slate-400 mt-0.5" />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Empresa emisora</p>
            <p className="font-semibold text-slate-900">{EMPRESA.nombre} · {EMPRESA.persona}</p>
            <p className="text-sm text-slate-600">DNI/NIF: {EMPRESA.dni} · {EMPRESA.direccion}</p>
          </div>
        </div>
      </div>

      {/* Observaciones */}
      <div className="px-6 md:px-8 py-4">
        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Observaciones / Notas</p>
        <p className="text-slate-700 whitespace-pre-wrap min-h-[1.5rem]" data-testid="doc-observaciones">
          {a.observaciones || "—"}
        </p>
      </div>

      {/* Firmas */}
      <div className="px-6 md:px-8 pb-8 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Signature title="Firma de Recogida" src={a.firmaRecogida} testid="doc-firma-recogida" />
        <Signature title="Firma de Entrega" src={a.firmaEntrega} testid="doc-firma-entrega" />
      </div>
    </div>
  );
};

const Signature = ({ title, src, testid }) => (
  <div className="rounded-xl border border-slate-200 p-3">
    <div className="h-28 flex items-center justify-center bg-slate-50 rounded-lg overflow-hidden">
      {src ? (
        <img src={src} alt={title} className="h-full object-contain" data-testid={testid} />
      ) : (
        <span className="text-xs text-slate-300">Sin firmar</span>
      )}
    </div>
    <p className="text-center text-sm font-semibold text-slate-700 mt-2 pt-2 border-t border-slate-200">{title}</p>
  </div>
);
